import {
  LIMITS,
  defaultListTitle,
  LIST_OCCASIONS,
  LIST_TEMPLATES,
  LIST_THEMES,
  OCCASION_CHOICES,
  OCCASION_CONFIG,
  TEMPLATE_CONFIG,
  type ListTemplate,
  type ListOccasion,
  type ListTheme,
} from "@listou/shared/constants";
import { STICKER_PACKS } from "@listou/shared/stickers";
import type { PublicList } from "@listou/shared/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { LastUsedBadge } from "../components/auth-layout";
import { ConfirmEmailStep, useEmailConfirmation, useSignWith } from "../components/confirm-email";
import { Divider } from "../components/divider";
import { withDraft, type LookDraft } from "../components/editor/appearance-tab";
import { PreviewPane } from "../components/editor/preview-pane";
import { TemplateThumb } from "../components/editor/template-thumb";
import { GoogleIcon } from "../components/google-icon";
import { ListPage } from "../components/list-page";
import { Sticker } from "../components/list-page/parts";
import { Logo } from "../components/logo";
import { PaletteSwatch } from "../components/palette-swatch";
import { SignatureForm, useNeedsSignature } from "../components/signature-form";
import { ChoiceGrid } from "../components/ui/choice-grid";
import { Spinner } from "../components/ui/spinner";
import { authApi, listsApi } from "../lib/api";
import { writeLastMethod } from "../lib/auth-ui";
import { sampleList, sampleWishes } from "../lib/demo";
import { clearDraft, readDraft, saveDraft } from "../lib/draft-store";
import { nextChristmas } from "../lib/format";
import { listsQuery } from "../lib/queries";
import { errorMessage } from "../lib/session";
import { motifOf, packStickerUrls, themeLabel } from "../lib/theme";

export const Route = createFileRoute("/nouvelle-liste")({
  // An occasion landing links here with its occasion, so the first step is already done.
  validateSearch: (
    search: Record<string, unknown>,
  ): { occasion?: ListOccasion; confirme?: boolean } => ({
    occasion: LIST_OCCASIONS.includes(search.occasion as ListOccasion)
      ? (search.occasion as ListOccasion)
      : undefined,
    // Set by the mailed link only: see the effect creating the list on the way back.
    confirme: search.confirme ? true : undefined,
  }),
  component: NewListPage,
});

const STEPS = ["Occasion", "Détails", "Style"] as const;
const DRAFT = "list";

type ListDraft = {
  occasion: ListOccasion;
  title: string;
  recipientName: string;
  eventDate: string;
  theme: ListTheme;
  template: ListTemplate;
  stickerPack: string | null;
  name: string;
  email: string;
};
const NO_PACK = "none";
/** Where the mailed link and Google bring the person back, to create the waiting draft. */
const BACK_TO_CREATE = "/nouvelle-liste?confirme=1";

function NewListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { occasion: preset, confirme } = Route.useSearch();
  // Client-only page, and only read once: a draft is what the mailed link comes back to.
  const [backFromLink] = useState(() => Boolean(confirme && readDraft<ListDraft>(DRAFT)));
  const createdFromLink = useRef(false);
  const [step, setStep] = useState(preset ? 1 : 0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [occasion, setOccasion] = useState<ListOccasion | null>(preset ?? null);
  const [title, setTitle] = useState("");
  const [recipientName, setRecipientName] = useState("");
  // Client-only page: reading today's date while rendering is safe here.
  const [eventDate, setEventDate] = useState(preset === "christmas" ? nextChristmas() : "");
  const [theme, setTheme] = useState<ListTheme>(
    preset ? OCCASION_CONFIG[preset].defaultTheme : "classic",
  );
  const [template, setTemplate] = useState<ListTemplate>(
    preset ? OCCASION_CONFIG[preset].defaultTemplate : "carnet",
  );
  const [stickerPack, setStickerPack] = useState<string | null>(
    preset ? OCCASION_CONFIG[preset].defaultStickerPack : "noel",
  );
  const [draft, setDraft] = useState<LookDraft>({});

  const config = occasion ? OCCASION_CONFIG[occasion] : null;
  const authConfig = useQuery({ queryKey: ["auth", "config"], queryFn: authApi.config });
  const needsSignature = useNeedsSignature();
  const confirmation = useEmailConfirmation({ redirectTo: BACK_TO_CREATE });
  const signWith = useSignWith();
  // After the look: the address, then the code, for someone without a real account.
  const [stage, setStage] = useState<"wizard" | "account" | "code">("wizard");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [resumed, setResumed] = useState(false);

  // Back from the mail's link, or a reload: the list comes back as it was left, on its
  // last step, ready to create.
  useEffect(() => {
    const saved = readDraft<ListDraft>(DRAFT);
    if (!saved) return;
    setOccasion(saved.occasion);
    setTitle(saved.title);
    setRecipientName(saved.recipientName);
    setEventDate(saved.eventDate);
    setTheme(saved.theme);
    setTemplate(saved.template);
    setStickerPack(saved.stickerPack);
    setName(saved.name);
    setEmail(saved.email);
    setStep(2);
    setResumed(true);
  }, []);

  // What the page will be called if the name stays empty; shown as the field's placeholder.
  const fallbackTitle = defaultListTitle(occasion ?? "other", recipientName.trim() || null);

  // The motif follows the chosen pack, the way the server defaults it, unless the template
  // starts plain. Sent explicitly: the server's default knows the occasion, not the template.
  const motifFor = (value: ListTemplate) =>
    stickerPack && !TEMPLATE_CONFIG[value].plainBackground
      ? { pack: stickerPack, layout: "semis" as const }
      : null;

  const createList = () =>
    listsApi.create({
      title: title.trim() || fallbackTitle,
      occasion: occasion ?? "other",
      recipientName: recipientName.trim() || null,
      eventDate: eventDate || null,
      theme,
      appearance: { template, stickerPack, motif: motifFor(template) },
    });

  const done = ({ list }: { list: { id: string } }) => {
    clearDraft(DRAFT);
    void queryClient.invalidateQueries({ queryKey: listsQuery.queryKey });
    void navigate({ to: "/mes-listes/$listId", params: { listId: list.id } });
  };

  const create = useMutation({ mutationFn: createList, onSuccess: done });

  /*
   * The mailed link proved the address: the person already asked for this list, so it is
   * created and opened without a second click. Only on that way back, never on a draft alone:
   * drafts do not expire, and coming back to this page days later must not create anything.
   * Once only: a failure falls back to the form, with its error and the draft intact.
   */
  useEffect(() => {
    if (!backFromLink || !resumed || confirmation.needsEmail || createdFromLink.current) return;
    createdFromLink.current = true;
    // The first name is a nicety: failing to save it must not cost the list.
    void signWith(name)
      .catch(() => undefined)
      .then(() => create.mutate());
  });

  /** Keeps the list and mails the code: the list is created once the address is proven. */
  const keepDraft = () => {
    if (!occasion) return;
    saveDraft<ListDraft>(DRAFT, {
      occasion,
      title,
      recipientName,
      eventDate,
      theme,
      template,
      stickerPack,
      name,
      email,
    });
  };

  const sendCode = useMutation({
    mutationFn: async () => {
      if (!occasion) throw new Error("No occasion chosen");
      keepDraft();
      await confirmation.request(email);
      writeLastMethod("email");
    },
    onSuccess: () => setStage("code"),
  });

  // What the new list will look like, with whatever the pointer is over applied on top.
  const previewList: PublicList | null = occasion
    ? withDraft(
        {
          ...sampleList(occasion, {
            title: title.trim() || fallbackTitle,
            recipientName: recipientName.trim() || null,
            eventDate: eventDate || null,
          }),
          theme,
          accentColor: null,
          appearance: {
            template,
            font: null,
            motif: stickerPack && motifFor(template) ? motifOf(stickerPack, "semis") : null,
            stickerPack,
            stickers: stickerPack
              ? packStickerUrls(stickerPack).map((url, index) => ({ id: String(index), url }))
              : [],
          },
        },
        draft,
      )
    : null;

  const chooseOccasion = (value: ListOccasion) => {
    setOccasion(value);
    // The occasion's look is the sensible starting point; the next step can still change it.
    setTheme(OCCASION_CONFIG[value].defaultTheme);
    setTemplate(OCCASION_CONFIG[value].defaultTemplate);
    setStickerPack(OCCASION_CONFIG[value].defaultStickerPack);
    // Christmas has one date; a field left blank would be the one thing to fill in.
    // Coming back to pick another occasion drops that default, not a date typed by hand.
    setEventDate((current) =>
      value === "christmas"
        ? current || nextChristmas()
        : current === nextChristmas()
          ? ""
          : current,
    );
    setStep(1);
  };

  const creatingFromLink =
    backFromLink && !create.isError && (confirmation.checking || !confirmation.needsEmail);
  if (creatingFromLink) {
    return (
      <main className="grid min-h-dvh place-items-center">
        <Spinner />
      </main>
    );
  }

  if (stage === "code") {
    return (
      <ConfirmEmailStep
        email={email}
        outcome="votre liste est créée"
        confirm={async (code) => {
          await confirmation.verify(email, code);
          await signWith(name);
          done(await createList());
        }}
        onBack={() => setStage("account")}
      />
    );
  }

  if (stage === "account") {
    return (
      <main className="mx-auto grid max-w-md gap-6 px-4 py-16">
        <Logo />
        <header className="grid gap-2">
          <h1 className="text-4xl font-semibold">Votre liste est prête</h1>
          <p className="text-lg text-muted">
            Dernière étape : de quoi signer votre liste et la retrouver partout.
          </p>
        </header>
        {/* Not for a guest: it attaches an address to keep its lists, and signing in with
            Google instead would leave them on the guest account. */}
        {authConfig.data?.google && !confirmation.isGuest && (
          <>
            {/* A full navigation: OAuth needs the browser to follow the redirects. The
                draft is what the way back creates, with the name Google gives. */}
            <a
              href={authApi.googleUrl(BACK_TO_CREATE)}
              onClick={() => {
                keepDraft();
                writeLastMethod("google");
              }}
              className="btn-secondary relative min-h-13 w-full text-lg"
            >
              <LastUsedBadge method="google" />
              <GoogleIcon />
              Continuer avec Google
            </a>
            <Divider />
          </>
        )}
        <form
          className="relative grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            sendCode.mutate();
          }}
        >
          <LastUsedBadge method="email" />
          <label className="label text-base">
            Votre prénom
            <input
              className="input text-lg"
              required
              minLength={LIMITS.nameMin}
              maxLength={LIMITS.nameMax}
              autoComplete="given-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <span className="font-normal text-muted">
              « Une liste de {name.trim() || "…"} », sur la page que vos proches ouvrent.
            </span>
          </label>
          <label className="label text-base">
            Votre e-mail
            <input
              className="input text-lg"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          {sendCode.isError && (
            <p className="text-danger">{errorMessage(sendCode.error, "L'envoi a échoué.")}</p>
          )}
          <button type="submit" className="btn min-h-13 text-lg" disabled={sendCode.isPending}>
            {sendCode.isPending ? "Envoi…" : "Recevoir mon code"}
          </button>
          <button type="button" className="btn-ghost" onClick={() => setStage("wizard")}>
            Revenir à ma liste
          </button>
          <p className="text-center text-muted">
            Déjà un compte ? Même adresse, même code : votre liste s'y ajoute.
          </p>
        </form>
      </main>
    );
  }

  return (
    <div
      className={`mx-auto grid gap-8 px-4 py-8 sm:px-6 sm:py-12 ${step === 2 ? "max-w-6xl" : "max-w-3xl"}`}
    >
      <div className="justify-self-start">
        <Logo />
      </div>
      {resumed && !confirmation.needsEmail && (
        <p className="rounded-2xl bg-butter/40 px-4 py-3">
          Adresse confirmée. Votre liste vous attend : un dernier coup d'œil, puis créez-la.
        </p>
      )}
      <header className="grid gap-4">
        <ol className="flex flex-wrap gap-2" aria-label="Étapes">
          {STEPS.map((label, index) => (
            <li key={label}>
              <button
                type="button"
                disabled={index > step || (index > 0 && !occasion)}
                onClick={() => setStep(index)}
                aria-current={index === step ? "step" : undefined}
                className={`rounded-full px-4 py-1.5 font-bold transition ${
                  index === step
                    ? "bg-ink text-white"
                    : index < step
                      ? "bg-ink/10 text-ink"
                      : "bg-ink/5 text-muted"
                }`}
              >
                {index + 1}. {label}
              </button>
            </li>
          ))}
        </ol>
        <h1 className="text-4xl font-semibold sm:text-5xl">
          {step === 0 && "C'est pour quelle occasion ?"}
          {step === 1 && "Quelques détails"}
          {step === 2 && "Choisissez son allure"}
        </h1>
      </header>

      {step === 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {OCCASION_CHOICES.map((value) => (
            <li key={value}>
              <button
                type="button"
                onClick={() => chooseOccasion(value)}
                aria-pressed={occasion === value}
                className={`grid w-full justify-items-start gap-4 rounded-3xl border-[1.5px] p-5 text-left transition hover:border-ink/40 ${
                  occasion === value ? "border-accent bg-accent/5" : "border-line bg-surface"
                }`}
              >
                <Sticker
                  src={packStickerUrls(OCCASION_CONFIG[value].defaultStickerPack)[0]}
                  className="size-20 object-contain"
                />
                <span className="font-display text-2xl font-bold">
                  {OCCASION_CONFIG[value].label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {step === 1 && config && (
        <form
          className="panel grid gap-5 p-6 sm:p-8"
          onSubmit={(event) => {
            event.preventDefault();
            setStep(2);
          }}
        >
          <label className="label">
            Nom de la liste
            <input
              className="input text-lg"
              value={title}
              maxLength={LIMITS.listTitleMax}
              placeholder={fallbackTitle}
              onChange={(event) => setTitle(event.target.value)}
              autoFocus
            />
          </label>
          {config.recipientLabel && (
            <label className="label">
              {config.recipientLabel}
              <input
                className="input text-lg"
                value={recipientName}
                maxLength={LIMITS.nameMax}
                placeholder="Facultatif"
                onChange={(event) => setRecipientName(event.target.value)}
              />
            </label>
          )}
          <label className="label">
            Date de l'événement
            <input
              className="input text-lg"
              type="date"
              value={eventDate}
              onChange={(event) => setEventDate(event.target.value)}
            />
            <span className="font-normal text-muted">
              Elle affiche un compte à rebours sur la page de la liste.
            </span>
          </label>
          <div className="flex justify-between gap-3">
            <button type="button" className="btn-ghost" onClick={() => setStep(0)}>
              Retour
            </button>
            <button type="submit" className="btn px-6">
              Continuer
            </button>
          </div>
        </form>
      )}
      {step === 1 && config && needsSignature && <SignatureForm />}

      {step === 2 && occasion && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          <PreviewPane
            label="Aperçu de votre page"
            className="lg:order-2"
            open={previewOpen}
            onOpenChange={setPreviewOpen}
          >
            <ListPage list={previewList as PublicList} wishes={sampleWishes(occasion)} preview />
          </PreviewPane>

          {/* Flex, not grid: a sticky grid item only moves within its own row, so the bar with
              the create button would stay at the very bottom of this long column. */}
          <div className="flex min-w-0 flex-col gap-6 lg:order-1">
            <section className="grid gap-3">
              <h2 className="text-xl font-semibold">La mise en page</h2>
              <ChoiceGrid<ListTemplate>
                label="Modèle de page"
                value={template}
                onChange={setTemplate}
                onPreview={(value) =>
                  setDraft(value ? { template: value, motif: motifFor(value) } : {})
                }
                className="grid-cols-2"
                options={LIST_TEMPLATES.map((value) => ({
                  value,
                  label: TEMPLATE_CONFIG[value].label,
                  content: (
                    <>
                      <TemplateThumb
                        template={value}
                        theme={theme}
                        accent={null}
                        stickers={packStickerUrls(stickerPack ?? "")}
                      />
                      <span className="grid gap-0.5 px-1 pb-1">
                        <span className="font-bold">{TEMPLATE_CONFIG[value].label}</span>
                        <span className="text-sm leading-snug text-muted">
                          {TEMPLATE_CONFIG[value].description}
                        </span>
                      </span>
                    </>
                  ),
                }))}
              />
            </section>

            <section className="grid gap-3">
              <h2 className="text-xl font-semibold">Les couleurs</h2>
              <ChoiceGrid<ListTheme>
                label="Palette"
                value={theme}
                onChange={setTheme}
                onPreview={(value) => setDraft(value ? { theme: value } : {})}
                className="grid-cols-4 sm:grid-cols-5 lg:grid-cols-4"
                cardClassName="justify-items-center rounded-3xl p-2 text-center"
                options={LIST_THEMES.map((value) => ({
                  value,
                  label: themeLabel(value),
                  content: (
                    <>
                      <PaletteSwatch theme={value} />
                      <span className="text-xs leading-tight font-bold">{themeLabel(value)}</span>
                    </>
                  ),
                }))}
              />
            </section>

            <section className="grid gap-3">
              <h2 className="text-xl font-semibold">Les stickers</h2>
              <ChoiceGrid<string>
                label="Pack de stickers"
                value={stickerPack ?? NO_PACK}
                onChange={(value) => setStickerPack(value === NO_PACK ? null : value)}
                onPreview={(value) =>
                  setDraft(value === null ? {} : { stickerPack: value === NO_PACK ? null : value })
                }
                className="grid-cols-3"
                options={[
                  {
                    value: NO_PACK,
                    label: "Sans stickers",
                    content: (
                      <>
                        <span className="grid aspect-square place-items-center rounded-xl bg-ink/[0.04] px-2 text-center text-sm font-bold text-muted">
                          Aucun dessin
                        </span>
                        <span className="px-1 pb-1 text-sm font-bold">Sans stickers</span>
                      </>
                    ),
                  },
                  ...STICKER_PACKS.toSorted(
                    // The occasion's packs first: a birth list should not open on pirates.
                    (a, b) =>
                      Number(b.occasions.some((o) => o === occasion)) -
                      Number(a.occasions.some((o) => o === occasion)),
                  ).map((pack) => ({
                    value: pack.id,
                    label: pack.label,
                    content: (
                      <>
                        <span className="grid aspect-square grid-cols-3 gap-0.5 rounded-xl bg-ink/[0.04] p-1">
                          {packStickerUrls(pack.id).map((url) => (
                            <Sticker
                              key={url}
                              src={url}
                              className="aspect-square w-full object-contain"
                            />
                          ))}
                        </span>
                        <span className="truncate px-1 pb-1 text-sm font-bold">{pack.label}</span>
                      </>
                    ),
                  })),
                ]}
              />
            </section>

            {create.isError && (
              <p className="text-danger">
                {errorMessage(create.error, "La liste n'a pas pu être créée.")}
              </p>
            )}
            <div className="sticky bottom-3 z-10 flex justify-between gap-3 rounded-full bg-surface p-2 shadow-[0_10px_32px_rgb(0_0_0/0.22)] ring-1 ring-ink/15">
              <button type="button" className="btn-ghost" onClick={() => setStep(1)}>
                Retour
              </button>
              {/* On a phone the preview opens from this bar, not from a button over it. */}
              <button
                type="button"
                className="btn-secondary px-4 lg:hidden"
                onClick={() => setPreviewOpen(true)}
              >
                Aperçu
              </button>
              <button
                type="button"
                className="btn min-h-12 px-5 text-lg sm:px-7"
                onClick={() => (confirmation.needsEmail ? setStage("account") : create.mutate())}
                disabled={create.isPending}
              >
                {create.isPending ? "Création…" : "Créer la liste"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
